import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UserTypes } from '@shared/enums/common.enum';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, combineLatest, filter, map, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
    isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
    isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

    isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
    isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

    canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
        this.isAuthenticated$,
        this.isDoneLoading$,
    ]).pipe(map((values) => values.every((b) => b)));

    constructor(
        private oauthService: OAuthService,
        private router: Router,
        private http: HttpClient
    ) {
        // Useful for debugging:
        this.oauthService.events.subscribe((event) => {
            if (event instanceof OAuthErrorEvent) {
                console.error('OAuthErrorEvent Object:', event);
            } else {
                // console.warn('OAuthEvent Object:', event);
            }
        });

        // This is tricky, as it might cause race conditions (where access_token is set in another
        // tab before everything is said and done there.
        // TODO: Improve this setup. See: https://github.com/jeroenheijmans/sample-angular-oauth2-oidc-with-auth-guards/issues/2
        window.addEventListener('storage', (event) => {
            // The `key` is `null` if the event was caused by `.clear()`
            if (event.key !== 'access_token' && event.key !== null) {
                return;
            }

            this.isAuthenticatedSubject$.next(
                this.oauthService.hasValidAccessToken()
            );

            if (!this.oauthService.hasValidAccessToken()) {
                setTimeout(() => {
                    this.navigateToLoginPage();
                }, 100);
            }
        });

        this.oauthService.events.subscribe((_) => {
            this.isAuthenticatedSubject$.next(
                this.oauthService.hasValidAccessToken()
            );
        });

        this.oauthService.events
            .pipe(filter((e) => ['token_received'].includes(e.type)))
            .subscribe((e) => this.oauthService.loadUserProfile());

        this.oauthService.events
            .pipe(
                filter((e) =>
                    [
                        'session_terminated',
                        'session_error',
                        'token_refresh_error',
                    ].includes(e.type)
                )
            )
            .subscribe((e) => this.navigateToLoginPage());
    }

    get isAuthorised(): any {
        return this.oauthService.hasValidAccessToken();
    }

    get userName(): string {
        if (this.isAuthorised) {
            const claims = this.oauthService.getIdentityClaims();
            // @ts-ignore
            return claims.name; // there will be userId
        }
        return 'not authenticated';
    }

    get userType(): string {
        if (this.isAuthorised) {
            const claims = this.oauthService.getIdentityClaims();
            return claims?.userType ?? 'not authenticated';
        }
        return 'not authenticated';
    }

    public get accessToken() {
        return this.oauthService.getAccessToken();
    }
    public get currentUserId() {
        return this.oauthService.getIdentityClaims()?.sub;
    }

    public get refreshToken() {
        return this.oauthService.getRefreshToken();
    }

    public get idToken() {
        return this.oauthService.getIdToken();
    }

    public get logoutUrl() {
        return this.oauthService.logoutUrl;
    }

    public get isEmployee() {
        return this.userType === UserTypes.Employee;
    }

    public get isPlayer() {
        return this.userType === UserTypes.Customer;
    }

    navigateToLoginPage() {
        this.login(location.pathname);
    }

    public runInitialLoginSequence(): Promise<void> {
        return (
            this.oauthService
                .loadDiscoveryDocument()

                // 1. HASH LOGIN:
                // Try to log in via hash fragment after redirect back
                // from IdServer from initImplicitFlow:
                .then(() =>
                    this.oauthService.tryLogin({
                        onTokenReceived: (context) => {
                            //
                            // Output just for purpose of demonstration
                            // Don't try this at home ... ;-)
                            //
                            // console.log('logged in');
                            // console.debug(context);
                        },
                    })
                )
                .then(() => {
                    this.isDoneLoadingSubject$.next(true);

                    if (this.oauthService.state) {
                        let stateUrl = this.oauthService.state;
                        if (!stateUrl.startsWith('/')) {
                            stateUrl = decodeURIComponent(stateUrl);
                        }
                        this.router.navigateByUrl(stateUrl);
                    }
                })
                .catch(() => this.isDoneLoadingSubject$.next(true))
        );
    }

    login(targetUrl?: string) {
        // Note: before version 9.1.0 of the library you needed to
        // call encodeURIComponent on the argument to the method.
        this.oauthService.initLoginFlow(targetUrl || this.router.url);
    }

    logout() {
        this.oauthService.logOut();
    }

    refresh() {
        this.oauthService.silentRefresh();
    }

    hasValidToken() {
        return this.oauthService.hasValidAccessToken();
    }
}
